import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import React from 'react';
// @mui
import {
  DataGridPro,
  GridColDef,
  GridToolbar,
  GridColumnVisibilityModel,
  GridLogicOperator,
  useGridApiRef,
  GridSortModel,
  GridFilterModel,
  GridPaginationModel,
  GridColType,
  GridAlignment,
} from '@mui/x-data-grid-pro';
import { Box, Stack, useTheme, useMediaQuery, width, minWidth, maxWidth } from '@mui/system';
import { LinearProgress, Typography, Tooltip, IconButton, Button, Menu, MenuItem, ListItemIcon, ListItemText, Divider, TextField, InputAdornment, Chip, CircularProgress, Grid, Theme } from '@mui/material';
import { fNumber, fPercent } from 'src/utils/format-number';
import { setFilterKeyword, setSelectedKeywords } from 'src/context/reducers/filter-settings/filterSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useLocales } from 'src/locales';
import { useResponsive } from 'src/hooks/use-responsive';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { formatDate } from 'src/utils/dates-labels';
import { brandedCampaignsVisibility, isMetricAllowed } from 'src/utils/branded-campaigns';
import { formatCurrency } from 'src/utils/format-currency';
import FilterPanelCustom from './filter-panel-custom/filter-panel-custom';
import { useLocation } from 'react-router-dom';
import ReactDOM from 'react-dom';
import CompareChip from 'src/components/chip/compare-chip';
import DatagridDiffCell from './data-grid-diff-cell';

import "./data-grid-custom.css";
import { i18n } from 'i18next';
import { RootState } from 'src/context/store';
import { get, merge } from 'lodash';
import { useKpisQuery } from 'src/context/api/data/api';
import { fDate } from 'src/utils/format-time';
import Iconify from '../iconify';
import HoverShortcut from 'src/components/visibility/components/HoverShortcut';
import CustomTooltip from 'src/components/visibility/components/CustomTooltip';
import { FlagIcon } from 'src/components/iconify/flagIcon';
import countriesJson from 'src/utils/countries.json';
import { renderMetricCell, renderBrandTrafficCell, formatComparePct, formatCompareDiff, compareColorPct, BRAND_TRAFFIC_3WAY_COLORS } from './data-grid-performance-split-business-overview';
import { compareKindForFormat } from './compare-kind';
import { columnOrderIndex } from './column-order';

export type MetricBarType = 'magnitude' | 'composition' | 'none';

// Resolve the in-cell bar type for a metric column. When a grid opts into per-metric bars
// (`metricBarTypes` provided), a metric WITHOUT an explicit entry falls back to 'none' so it still
// renders through MetricValueCell (value + inline compare chip) instead of the bare `cell()`.
// Without that fallback, columns hidden by default in Explore (not listed in GRID_BAR_TYPES) lost
// their inline comparison once revealed. Grids that don't pass `metricBarTypes` keep `undefined`
// (legacy `cell()` rendering, unchanged).
export const resolveBarType = (
  metricBarTypes: Record<string, MetricBarType> | undefined,
  key: string,
  isDimension: boolean,
): MetricBarType | undefined => {
  if (isDimension || !metricBarTypes) return undefined;
  return metricBarTypes[key] ?? 'none';
};

// ----------------------------------------------------------------------


export enum ColumnType {
  COMPARISON = 'comparison',
  DIFF = 'diff',
  LINE = 'line',
}

const valueOrZero = (value: any) => value ?? 0;
const valueOrDate = (value: any) => value ?? fDate(new Date(), "yyyy-MM-dd");
const valueOrEmpty = (value: any) => value ?? "";

const useGetFormatter = (kpi: ReturnType<typeof useKpisQuery>["data"][number]) => {
  const { currentLang: { value: locale } } = useLocales();
  const workspace = useCurrentWorkspace();
  const [formatter, nbDecimals] = kpi?.format?.split(":") ?? ["", 0];
  const nbDecimalNumber = parseInt(nbDecimals);
  const formatterFunction = ["date", "date_day"].includes(kpi.id) ? (value: any = "") => {
    return fDate(valueOrDate(value), "yyyy-MM-dd")
  } :
    ["device", "device_type"].includes(kpi.id) ? (value: any = "") => {
      value = valueOrEmpty(value);
      return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
    } :
      formatter === "currency" ? (value: number = 0) => {
        return Intl.NumberFormat(locale, {
          style: "currency",
          currency: workspace?.currency,
          minimumFractionDigits: nbDecimalNumber,
          maximumFractionDigits: nbDecimalNumber,
          notation: value > 1000 ? "compact" : "standard",
        }).format(valueOrZero(value));
      } : formatter === "percentage" ? (value: number = 0) => ((valueOrZero(value))?.toFixed(nbDecimalNumber) ?? 0) + "%" : ["number", "float"].includes(formatter) ? (value: number = 0) => {
        return valueOrZero(value).toLocaleString(locale, { maximumFractionDigits: nbDecimalNumber })
      } : (value: any) => value;
  return formatterFunction;
}

const ProgressCell = ({ row, kpi }: { row: any, kpi: any }) => {
  const theme = useTheme();
  const cappedValue = Math.min(row[kpi.id], 100); // Limiter à 100%
  return (
    <Stack spacing={1} direction="row" alignItems="center" sx={{ px: 1, width: 1, height: 1 }}>
      <LinearProgress
        value={cappedValue}
        variant="determinate"
        sx={{
          width: 1,
          height: 6,
          backgroundColor: theme.palette.mode === "dark" ? kpi["color:dark"] : kpi["color:light"],
          '& .MuiLinearProgress-bar': {
            backgroundColor: kpi["color"],
          }
        }}
      />
      <Typography variant="caption" sx={{ width: 80, fontSize: "0.825rem" }}>
        {cappedValue === 0 ? '0%' : fPercent(cappedValue)}
      </Typography>
    </Stack>
  )
}

type DiffCellProps = {
  kpi: ReturnType<typeof useKpisQuery>["data"][number];
  value: number;
  compare: number;
}

const DiffCell = ({ value, compare, kpi }: DiffCellProps) => {
  const [format, nbDecimals] = kpi.format.split(":") ?? ["", 0];

  const diff = (value - compare);
  const diffPercentage = (value - compare) / compare * 100;

  return <DatagridDiffCell
    value={parseFloat(diff.toFixed(nbDecimals))}
    compareChip={diffPercentage}
    invertedColor={kpi.invert}
    unit={format === "percentage" ? "pp" : format === "float" ? "" : "currency"}
  />

}

const BaseCell = ({ row, kpi }: { row: any, kpi: any }) => {
  const formatterFunction = useGetFormatter(kpi);
  return <Typography variant="caption" sx={{ width: 80, fontSize: kpi.viz === "dimension" ? "0.85rem" : "0.825rem" }}>
    {formatterFunction(row[kpi.id])}
  </Typography>
}

// Country dimension cell: flag + name, aligned with the country selector bar
// (same circle-flags source as the header country dropdown). Opt-in via
// `showDimensionFlags` so other grids stay unchanged.
const isCountryDimension = (id: string) => ['region_name', 'country'].includes(id);

const CountryDimCell = ({ value }: { value: string }) => {
  const entry = (countriesJson as Array<{ name: string; code: string }>).find((c) => c.name === value);
  const code = entry?.code;
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1, width: 1, height: 1 }}>
      {code && <FlagIcon code={code} />}
      <Typography variant="caption" sx={{ fontSize: '0.85rem' }}>{value}</Typography>
    </Stack>
  );
};

// Dimension cell with an opt-in "apply filter" hover shortcut (Explore search terms). Reuses the
// Visibility `HoverShortcut` affordance : le terme reste visible, et au survol de la cellule un
// raccourci « Filter » apparaît à droite. Le clic (dé)sélectionne la valeur dans le filtre keyword
// du header (setFilterKeyword + setSelectedKeywords → chip supprimable + checkbox synchronisés).
// Quand la valeur est déjà filtrée, le raccourci reste affiché (état actif) et bascule vers « Remove ».
const DimensionFilterCell = ({ value }: { value: any }) => {
  const dispatch = useDispatch();
  const { t } = useLocales();
  const keywords: string[] = useSelector((state: RootState) => (state as any).filters?.keywords) || [];
  const [hovered, setHovered] = useState(false);
  const isActive = value != null && value !== '' && keywords.includes(value);

  const toggle = () => {
    if (value == null || value === '') return;
    const updated = isActive ? keywords.filter((kw) => kw !== value) : [...keywords, value];
    dispatch(setFilterKeyword(updated));
    dispatch(setSelectedKeywords(updated));
  };

  // Raccourci au survol, rendu identique au SearchTermCell de la datagrid Search Term Analysis :
  // label + flèche `eva:diagonal-arrow-right-up-fill`, couleur #006C9C native au HoverShortcut.
  const shortcut = (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
      {t(isActive ? 'dashboard.filters.remove_filter' : 'dashboard.filters.apply_filter')}
      <Iconify icon="eva:diagonal-arrow-right-up-fill" width={14} />
    </Box>
  );

  // Box model identique aux cellules métriques (Stack width:1/height:1, alignItems center, justify
  // par défaut flex-start) → le terme reste calé à gauche et verticalement centré comme le reste de
  // la ligne ; le raccourci est poussé à droite via ml:auto (pas de space-between qui décalait le rendu).
  return (
    <Stack
      direction="row"
      alignItems="center"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{ gap: '8px', width: 1, height: 1 }}
    >
      <CustomTooltip title={value ?? ''} placement="top">
        <Typography
          variant="body2"
          sx={{ fontSize: 14, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {value}
        </Typography>
      </CustomTooltip>
      <HoverShortcut
        defaultContent=""
        hoverContent={shortcut}
        onClick={toggle}
        forceHovered={hovered || isActive}
        fontSize={14}
        sx={{ ml: 'auto', flexShrink: 0 }}
      />
    </Stack>
  );
};

// Value-only metric cell (barType 'none', e.g. Blended CPC ratio). Mirrors the value
// typography of the bar cells so the column stays visually consistent (no smaller caption font).
const MetricValueCell = React.memo(({ row, kpi, fieldKey, valueAlign = 'right', showCompare = false, evolutionMode = true }: { row: any; kpi: any; fieldKey: string; valueAlign?: 'left' | 'right'; showCompare?: boolean; evolutionMode?: boolean }) => {
  const fmt = useGetFormatter(kpi);
  const { currentLang: { value: locale } } = useLocales();
  const workspace = useCurrentWorkspace();
  const value = row[fieldKey] ?? 0;
  const compare = row.compare?.[fieldKey];
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent={valueAlign === 'left' ? 'flex-start' : 'flex-end'}
      sx={{ gap: '8px', width: 1, height: 1 }}
    >
      <Typography
        sx={{
          textAlign: valueAlign,
          fontSize: '14px',
          fontWeight: 500,
          lineHeight: '22px',
          color: '#333C47',
          whiteSpace: 'nowrap',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {fmt(value)}
      </Typography>
      {showCompare && <Box sx={{ flex: 1 }} />}
      {showCompare && (
        <Typography
          sx={{
            flexShrink: 0,
            textAlign: 'right',
            fontSize: '12px',
            fontWeight: 400,
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            fontVariantNumeric: 'tabular-nums',
            color: compareColorPct(value, compare, kpi.invert),
          }}
        >
          {evolutionMode
            ? formatComparePct(value, compare)
            : formatCompareDiff(value, compare, compareKindForFormat(kpi.format), { currency: workspace?.currency, locale })}
        </Typography>
      )}
    </Stack>
  );
});

// Metric cell with a proportional magnitude bar (value ÷ page-max), reusing the
// tested renderer from the performance-split grid. Opt-in via `metricBarTypes`.
const MetricBarCell = React.memo(({ row, kpi, fieldKey, max, valueAlign = 'right', barColor, showCompare = false, evolutionMode = true }: { row: any; kpi: any; fieldKey: string; max: number; valueAlign?: 'left' | 'right'; barColor?: string; showCompare?: boolean; evolutionMode?: boolean }) => {
  const fmt = useGetFormatter(kpi);
  const { currentLang: { value: locale } } = useLocales();
  const workspace = useCurrentWorkspace();
  const value = row[fieldKey] ?? 0;
  // Size the value slot to the column's widest value (the page-max the bar scales to) so a
  // large number never overflows onto the bar. The width is derived from `max` alone, so it's
  // identical for every row → all bars stay aligned. `ch` pairs with the tabular-nums digits;
  // +1 covers narrower glyphs (commas / currency symbols). Floored at 7ch (~the old 60px).
  const valueWidth = `${Math.max(7, fmt(max).length + 1)}ch`;
  return renderMetricCell({
    displayValue: fmt(value),
    barValue: value,
    max,
    barColor: barColor ?? kpi.color,
    showCompare,
    current: value,
    compare: row.compare?.[fieldKey],
    invertedColor: kpi.invert,
    evolutionMode,
    compareKind: compareKindForFormat(kpi.format),
    currency: workspace?.currency,
    locale,
    valueWidth,
    valueAlign,
  });
});

// Brand Traffic cell: 100%-stacked organic/paid/uncaptured composition bar. Falls back to a
// plain magnitude bar when the composition fields are absent from the row (graceful degradation).
const CompositionBarCell = React.memo(({ row, kpi, fieldKey, max, valueAlign = 'right', showCompare = false, evolutionMode = true, mode = 'organic-paid' }: { row: any; kpi: any; fieldKey: string; max: number; valueAlign?: 'left' | 'right'; showCompare?: boolean; evolutionMode?: boolean; mode?: 'organic-paid' | 'organic-search-shopping' }) => {
  const fmt = useGetFormatter(kpi);
  const { t } = useLocales();
  const workspace = useCurrentWorkspace();
  const value = row[fieldKey] ?? 0;

  // Découpage 3-way (Explore) : Organic / Paid Search / Paid Shopping (shopping + pmax regroupés).
  // Fallback gracieux vers le 2-way ci-dessous si les champs 3-way sont absents de la ligne.
  if (mode === 'organic-search-shopping') {
    // Le back (traffic table.sql) expose les clics sous les alias *_brand_traffic ; on garde les
    // noms bruts ad_clicks_from_* en fallback. shopping_brand_traffic vaut -1 quand is_shopping=FALSE
    // (template lead sans shoppingIncluded) => clampé à 0 par Math.max.
    // Shopping / PMax ne contribuent que si leurs campagnes brandées sont configurées
    // dans le workspace ; sans aucune des deux, le segment Paid Shopping disparaît.
    const { showShopping, showPmax } = brandedCampaignsVisibility(workspace);
    const organic3 = row.organic_clicks ?? row.organic_brand_traffic;
    const paidSearch = row.ad_clicks_from_search ?? row.search_brand_traffic;
    const shopping = showShopping ? row.ad_clicks_from_shopping ?? row.shopping_brand_traffic ?? 0 : 0;
    const pmax = showPmax ? row.ad_clicks_from_pmax ?? row.pmax_brand_traffic ?? 0 : 0;
    const paidShopping = Math.max(0, shopping) + Math.max(0, pmax);
    const total3 =
      Math.max(0, organic3 ?? 0) + Math.max(0, paidSearch ?? 0) + Math.max(0, paidShopping ?? 0);
    if (total3 > 0) {
      return renderBrandTrafficCell({
        displayValue: fmt(value),
        showCompare,
        current: value,
        compare: row.compare?.[fieldKey],
        evolutionMode,
        t,
        valueAlign,
        segments: [
          { label: 'Organic Traffic', value: organic3, color: BRAND_TRAFFIC_3WAY_COLORS.organic },
          { label: 'Paid Search Traffic', value: paidSearch, color: BRAND_TRAFFIC_3WAY_COLORS.paidSearch },
          // Segment groupé shopping + pmax : étiquette PMax quand seul PMax est configuré.
          ...(showShopping || showPmax
            ? [{ label: showShopping ? 'Paid Shopping Traffic' : 'Paid PMax Traffic', value: paidShopping, color: BRAND_TRAFFIC_3WAY_COLORS.paidShopping }]
            : []),
        ],
      });
    }
  }

  const organic = row.organic_brand_traffic;
  const paid = row.paid_brand_traffic ?? row.ad_brand_traffic;
  const uncaptured = row.uncaptured_brand_traffic;
  const hasComposition =
    Math.max(0, organic ?? 0) + Math.max(0, paid ?? 0) + Math.max(0, uncaptured ?? 0) > 0;
  if (!hasComposition) {
    return (
      <MetricBarCell row={row} kpi={kpi} fieldKey={fieldKey} max={max} valueAlign={valueAlign} showCompare={showCompare} evolutionMode={evolutionMode} />
    );
  }
  return renderBrandTrafficCell({
    displayValue: fmt(value),
    organic,
    paid,
    uncaptured,
    showCompare,
    current: value,
    compare: row.compare?.[fieldKey],
    evolutionMode,
    t,
    valueAlign,
  });
});

// Cellules mémoïsées : lors d'un re-render de la grille (ex. toggle d'une colonne dans le panneau,
// tri, pagination), les props par cellule (row/kpi refs stables + primitives) sont inchangées, donc
// React.memo court-circuite le re-render de la centaine de cellules et de leurs hooks.
MetricValueCell.displayName = 'MetricValueCell';
MetricBarCell.displayName = 'MetricBarCell';
CompositionBarCell.displayName = 'CompositionBarCell';

const useMakeCell = (showDimensionFlags?: boolean, metricBarTypes?: Record<string, MetricBarType>, pageMax?: Record<string, number>, leftAlign?: boolean, metricBarColors?: Record<string, string>, inlineCompare?: boolean, evolutionMode?: boolean, columnLabelOverrides?: Record<string, string>, brandTrafficMode?: 'organic-paid' | 'organic-search-shopping', dimensionFilterField?: string, columnMinWidths?: Record<string, number>) => {
  const { currentLang: { value: locale } } = useLocales();
  const filterStore = useSelector((state: RootState) => state.filters);
  const lgUp = useResponsive('up', 'lg');
  const kpis = useKpisQuery().data ?? [];

  return (row: any, kpi: any, key: string) => {
    const compareEnabled = filterStore?.compare;
    const columns: (GridColDef & { hide?: boolean })[] = [];

    // Libellé d'en-tête : override par champ (opt-in) sinon titre du catalogue KPI.
    const label = columnLabelOverrides?.[key] ?? (kpi[locale as keyof typeof kpis[0]] as string);

    const [type, align]: [GridColType, GridAlignment] =
      kpi.viz === "dimension" ? ["string", "left"] : ["number", "right"];

    const Progress =
      kpi.format.split(":").includes("percentage") &&
        ["date", "date_day", "year_week", "year_month"].some((item) =>
          Object.keys(row).includes(item)
        )
        ? ProgressCell
        : BaseCell;

    const cell = (row: any) => <Progress row={row} kpi={kpi} />;

    const headerTextAlign = leftAlign ? "left" : "right";
    const HeaderCell = (suffix: string = "", compare: boolean = false) => (
      <div style={{ whiteSpace: "pre-line", display: "flex", flexDirection: "column", alignItems: leftAlign ? "flex-start" : "flex-end" }}>
        <span style={{ textAlign: headerTextAlign }}>
          {label} {suffix}
        </span>
        {compareEnabled && suffix === "Compare" && (
          <span style={{ textAlign: headerTextAlign }}>
            {formatDate(compare ? filterStore?.compareStart : filterStore?.start, locale)} -{" "}
            {formatDate(compare ? filterStore?.compareEnd : filterStore?.end, locale)}
          </span>
        )}
      </div>
    );

    // No-bar metric cells (e.g. Blended CPC ratio) render only a value, so they need
    // less horizontal room than bar cells. Opt-in via `metricBarTypes[key] === 'none'`.
    const isNoBarMetric = kpi.viz !== "dimension" && metricBarTypes?.[key] === "none";
    const [defaultMinWidth, maxWidth] = (isNoBarMetric ? [120, 160] : [190, 500]) as [number, number];
    // Per-field minWidth from the maquette (opt-in). Keeps flex:1 so columns still stretch to fill
    // the viewport, using the maquette width as the floor. Falls back to the generic default.
    const minWidth = columnMinWidths?.[key] ?? defaultMinWidth;

    const valueGetter =
      ["date", "date_day"].includes(key)
        ? (_: any, row: any) => formatDate(row[key], locale)
        : (_: any, row: any) => row[key];

    const isDate = ["date", "date_day", "year_week", "year_month"].some((item) =>
      Object.keys(row).includes(item)
    );
    // Inline date-comparison indicator inside each metric cell (Overview style), in place of the
    // separate Compare/Diff columns. Off on date rows (no compare counterpart) and when compare is off.
    const showInlineCompare = !!(inlineCompare && compareEnabled && kpi.viz !== "dimension" && !isDate);

    // ✅ Colonne principale
    columns.push({
      field: key,
      hide: kpi.viz !== "dimension" ? !kpi.show : false,
      align: leftAlign ? "left" : align,
      headerAlign: leftAlign ? "left" : undefined,
      type,
      headerName: label,
      minWidth,
      maxWidth,
      flex: lgUp ? 1 : undefined,
      renderHeader: () => HeaderCell(),
      renderCell: (params: any) => {
        if (showDimensionFlags && kpi.viz === "dimension" && isCountryDimension(kpi.id)) {
          return <CountryDimCell value={params.row[key]} />;
        }
        // Dimension avec raccourci « apply filter » au survol (opt-in Explore search terms).
        if (kpi.viz === "dimension" && dimensionFilterField && key === dimensionFilterField) {
          return <DimensionFilterCell value={params.row[key]} />;
        }
        const barType = resolveBarType(metricBarTypes, key, kpi.viz === "dimension");
        if (barType === "magnitude") {
          return <MetricBarCell row={params.row} kpi={kpi} fieldKey={key} max={pageMax?.[key] ?? 0} valueAlign={leftAlign ? "left" : "right"} barColor={metricBarColors?.[key]} showCompare={showInlineCompare} evolutionMode={evolutionMode} />;
        }
        if (barType === "composition") {
          return <CompositionBarCell row={params.row} kpi={kpi} fieldKey={key} max={pageMax?.[key] ?? 0} valueAlign={leftAlign ? "left" : "right"} showCompare={showInlineCompare} evolutionMode={evolutionMode} mode={brandTrafficMode} />;
        }
        if (barType === "none") {
          return <MetricValueCell row={params.row} kpi={kpi} fieldKey={key} valueAlign={leftAlign ? "left" : "right"} showCompare={showInlineCompare} evolutionMode={evolutionMode} />;
        }
        return cell(params.row);
      },
      valueGetter,
    });

    // ✅ Colonnes Compare et Diff si activé (sautées quand le compare est rendu inline)
    if (compareEnabled && kpi.viz !== "dimension" && !isDate && !inlineCompare) {
      // Compare
      columns.push({
        field: `${key}Compare`,
        hide: !kpi.show,
          align: leftAlign ? "left" : align,
        headerAlign: leftAlign ? "left" : undefined,
        type,
        headerName: `${label} Compare`,
        minWidth,
        maxWidth,
        flex: lgUp ? 1 : undefined,
        renderHeader: () => HeaderCell("Compare", true),
        renderCell: (params: any) => cell(params.row.compare),
        valueGetter: (_, row: any) => row.compare[key],
      });

      // Diff
      columns.push({
        field: `${key}Diff`,
        hide: !kpi.show,
        align: leftAlign ? "left" : align,
        headerAlign: leftAlign ? "left" : undefined,
        type,
        headerName: `${label} Difference`,
        minWidth: 225,
        width: 225,
        maxWidth: 350,
        flex: lgUp ? 1 : undefined,
        renderHeader: () => HeaderCell("Difference"),
        renderCell: (params: any) => (
          <DiffCell value={params.row[key]} compare={params.row.compare[key]} kpi={kpi} />
        ),
        valueGetter: (_, row: any) => row.diff?.[key] ?? 0,
      });
    }

    return columns;
  };
};


const useCreateColumns = <T extends GridColDef>(data: any, excludeColumns: string[] = [], showDimensionFlags?: boolean, metricBarTypes?: Record<string, MetricBarType>, leftAlign?: boolean, metricBarColors?: Record<string, string>, inlineCompare?: boolean, evolutionMode?: boolean, columnOrder?: string[], columnLabelOverrides?: Record<string, string>, brandTrafficMode?: 'organic-paid' | 'organic-search-shopping', dimensionFilterField?: string, columnMinWidths?: Record<string, number>): T[] => {
  const { data: kpis = [] } = useKpisQuery();
  const { currentLang: { value: locale } } = useLocales();
  const lgUp = useResponsive('up', 'lg');
  const filterStore = useSelector((state: RootState) => state.filters);
  const workspace = useCurrentWorkspace();

  // Page-relative max per bar'd metric column (magnitude/composition bars scale to the visible page).
  const pageMax = useMemo(() => {
    const maxes: Record<string, number> = {};
    if (metricBarTypes && Array.isArray(data) && data.length) {
      Object.keys(metricBarTypes).forEach((k) => {
        if (metricBarTypes[k] !== 'none') {
          maxes[k] = Math.max(0, ...data.map((r: any) => Number(r?.[k]) || 0));
        }
      });
    }
    return maxes;
  }, [data, metricBarTypes]);

  const makeCell = useMakeCell(showDimensionFlags, metricBarTypes, pageMax, leftAlign, metricBarColors, inlineCompare, evolutionMode, columnLabelOverrides, brandTrafficMode, dimensionFilterField, columnMinWidths);

  // Colonnes Shopping / PMax exclues quand les campagnes brandées correspondantes
  // ne sont pas configurées dans le workspace (elles n'ont alors aucune donnée).
  const brandedVisibility = brandedCampaignsVisibility(workspace);

  const definedColumns: T[] = useMemo(() => {
    const make_row = (row: any, acc: GridColDef[]) => {
      Object.keys(row).forEach((key) => {
        const kpi = kpis.find((kpi) => kpi.id === key);
        const activation = filterStore?.extraOptions?.mode === "activations";
        const kpiViz = (
          kpi?.viz === "both"
          || (activation && kpi?.viz === "activation")
          || (!activation && kpi?.viz === "base")
        );

        if (
          (kpi?.viz === "dimension" || kpiViz) && row[key] !== -1
          && isMetricAllowed(key, brandedVisibility)) {
          const columns: GridColDef[] = makeCell(row, kpi, key);

          columns.forEach((column, i) => {
            const index = acc.findIndex((col: GridColDef) => col.field === column.field);

            if (index !== -1) {
              // Remplacer la colonne existante
              acc[index] = column;
            } else {
              acc.push(column);
            }
          });
        }
      });
      return acc;
    };

    return data.reduce((acc: GridColDef[], row: any) => make_row(row, acc), [] as GridColDef[]).sort((a: GridColDef, b: GridColDef) => {
      // Explicit maquette order (columnOrder) takes precedence; fields not listed there fall back
      // to the KPI catalog `order` and are placed after the explicitly ordered ones.
      const aIndex = columnOrderIndex(a.field, columnOrder);
      const bIndex = columnOrderIndex(b.field, columnOrder);
      if (aIndex !== -1 || bIndex !== -1) {
        const aRank = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
        const bRank = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;
        if (aRank !== bRank) return aRank - bRank;
      }
      const aPosition = kpis.find((kpi) => kpi.id === a.field.replace("Compare", "").replace("Diff", ""))?.order ?? 0;
      const bPosition = kpis.find((kpi) => kpi.id === b.field.replace("Compare", "").replace("Diff", ""))?.order ?? 0;
      return aPosition - bPosition;
    }) as unknown as T[];
  }, [workspace, filterStore?.compare, lgUp, locale, data, showDimensionFlags, metricBarTypes, leftAlign, metricBarColors, inlineCompare, evolutionMode, columnOrder, columnMinWidths]);


  return definedColumns;
};

// ----------------------------------------------------------------------

type Props = {
  data: {
    id: string;
    keyword: string;
    brandSearches?: number; // Add optional brandSearches property
    [key: string]: any; // Allow for other properties that might be used
  }[];
  paginationModel: any;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  paginationMode?: 'server';
  filterMode?: 'server';
  sortMode?: 'server';
  rowCount?: number;
  /** Affiche l'overlay de chargement natif du grid (refetch d'arrière-plan) sans démonter la grille.
      Additif : omis → comportement inchangé. */
  loading?: boolean;
  sortModel?: GridSortModel;
  filterModel?: GridFilterModel;
  onSortModelChange?: (model: GridSortModel) => void;
  onFilterModelChange?: (newModel: GridFilterModel) => void;
  pinnedColumns?: any[];
  onExport?: (columns: GridColDef[]) => void;
  visibilityModel?: GridColumnVisibilityModel;
  onVisibilityModelChange?: (model: GridColumnVisibilityModel) => void;
  /** Optional external grid apiRef. Defaults to an internal ref (existing call sites unchanged). */
  apiRef?: any;
  /** Hide the built-in GridToolbar (e.g. when the parent renders its own icon toolbar). Default false. */
  hideToolbar?: boolean;
  /** Base KPI fields visible by default; all other metric columns start hidden. Dimension/pinned
   *  columns always stay visible. Omit to keep the existing per-KPI `show` behavior. */
  defaultVisibleFields?: string[];
  /** Render a fixed-height grid with internal vertical scroll (like Competitors). Omit → autoHeight. */
  gridHeight?: number | string;
  /** Show a country flag next to country dimension values, aligned with the country bar. Default false. */
  showDimensionFlags?: boolean;
  /** Per-metric in-cell bar: 'magnitude' (value ÷ page-max), 'composition' (brand-traffic stacked),
   *  or 'none'. Keys are KPI field ids. Omit → no bars (existing behavior). */
  metricBarTypes?: Record<string, MetricBarType>;
  /** Row height forwarded to DataGridPro. Omit → MUI default (existing behavior). Prefer this over
   *  CSS height overrides so the virtualizer geometry stays correct (otherwise the first row clips). */
  rowHeight?: number;
  /** Column header height forwarded to DataGridPro. Omit → MUI default (existing behavior). */
  columnHeaderHeight?: number;
  /** Left-align the whole grid: column header labels AND cell values (incl. metric/numeric
   *  columns and in-cell bar values). Omit → existing behavior (metrics right-aligned). */
  leftAlign?: boolean;
  /** Per-metric override for the magnitude bar color (keys are KPI field ids). Omit a field →
   *  falls back to `kpi.color`. Only affects 'magnitude' bars (composition uses its own palette). */
  metricBarColors?: Record<string, string>;
  /** Inline date-comparison indicator inside each metric cell (Overview style) instead of the
   *  separate Compare/Diff columns. Only effective when a date comparison is active. */
  inlineCompare?: boolean;
  /** When inlineCompare is on: true → show % evolution, false → absolute difference. Default true. */
  evolutionMode?: boolean;
  /** Explicit left-to-right column order (KPI field ids). Listed fields are ordered first in this
   *  sequence; unlisted metric columns fall back to the KPI catalog `order`. Omit → catalog order. */
  columnOrder?: string[];
  /** Anchor element for the columns/filter panel popover. Set this to the clicked toolbar button's
   *  DOM node when the call site renders its own header buttons outside the grid (e.g. Explore with
   *  `hideToolbar`), so the panel opens under the button. Omit → MUI default anchoring (unchanged). */
  panelAnchorEl?: HTMLElement | null;
  /** Per-field column header label overrides (KPI field id → label). Omit a field → KPI catalog
   *  title (existing behavior). Lets a call site rename a column header without touching the catalog. */
  columnLabelOverrides?: Record<string, string>;
  /** Brand-traffic composition split for 'composition' bars. 'organic-paid' (default, existing 2-way)
   *  or 'organic-search-shopping' (Explore 3-way: organic_clicks / ad_clicks_from_search /
   *  ad_clicks_from_shopping+pmax). Falls back to 2-way if the 3-way fields are absent from the row. */
  brandTrafficMode?: 'organic-paid' | 'organic-search-shopping';
  /** Preserve the user's column show/hide choices across data refetches & column rebuilds
   *  (filter, sort, evolution toggle…). Default false → historical reset-to-defaults behavior. */
  persistColumnVisibility?: boolean;
  /** Opt-in drill-down (Explore) : la cellule dimension de ce champ (ex. 'search_term') affiche un
   *  raccourci « apply filter » au survol (HoverShortcut) qui (dé)sélectionne sa valeur dans le filtre
   *  keyword du header. Quand fourni, on désactive aussi le `onRowClick` historique (qui lisait
   *  `row.keyword` sur toute la ligne). Omis → comportement inchangé. */
  dimensionFilterField?: string;
  /** Per-field minWidth (KPI field id → px), taken from the Figma maquette. Used as the column's
   *  minWidth floor while flex:1 is kept, so columns still stretch to fill the viewport. Omit a
   *  field → generic default (190 bar / 120 no-bar). */
  columnMinWidths?: Record<string, number>;
};

export default function GenericDataGrid({
  data,
  paginationModel,
  onPaginationModelChange,
  paginationMode,
  rowCount,
  sortModel,
  onSortModelChange,
  onFilterModelChange,
  filterModel: filterModelProps,
  pinnedColumns,
  onExport,
  onVisibilityModelChange,
  visibilityModel: visibilityModelProps,
  apiRef: apiRefProp,
  hideToolbar,
  defaultVisibleFields,
  gridHeight,
  showDimensionFlags,
  metricBarTypes,
  rowHeight,
  columnHeaderHeight,
  leftAlign,
  metricBarColors,
  inlineCompare,
  evolutionMode,
  columnOrder,
  panelAnchorEl,
  columnLabelOverrides,
  brandTrafficMode,
  persistColumnVisibility,
  dimensionFilterField,
  columnMinWidths,
  loading,
}: Props) {
  const internalApiRef = useGridApiRef();
  const apiRef = apiRefProp ?? internalApiRef;
  const dispatch = useDispatch();
  const theme = useTheme();
  const filterStore = useSelector((state: any) => state.filters);
  const advancedView = filterStore?.isAdvancedView;

  const handleRowClick = (params: any) => {
    const keywords = filterStore?.keywords || [];
    const clickedKeyword = params?.row?.keyword;

    let updatedKeywords;

    if (keywords.includes(clickedKeyword)) {
      updatedKeywords = keywords.filter((keyword: string) => keyword !== clickedKeyword);
    } else {
      updatedKeywords = [...keywords, clickedKeyword]
    }

    dispatch(setFilterKeyword(updatedKeywords));
  };


  // Initialize grid with filters when mounted
  useEffect(() => {
    if ((filterModelProps?.items.length ?? 0) > 0 && apiRef.current) {
      apiRef.current.setFilterModel(filterModelProps as GridFilterModel);
    }
  }, [apiRef]);

  const columns = useCreateColumns(data, pinnedColumns, showDimensionFlags, metricBarTypes, leftAlign, metricBarColors, inlineCompare, evolutionMode, columnOrder, columnLabelOverrides, brandTrafficMode, dimensionFilterField, columnMinWidths);

  // Initial column visibility. With `defaultVisibleFields`, only those base metrics
  // (plus dimension/pinned columns) start visible; otherwise keep the per-KPI `show` flag.
  const visibleFor = useCallback((column: any) => {
    if (defaultVisibleFields && defaultVisibleFields.length) {
      const base = column.field.replace(/Compare$/, '').replace(/Diff$/, '');
      return (pinnedColumns ?? []).includes(column.field) || defaultVisibleFields.includes(base);
    }
    return column.hide === true ? false : true;
  }, [defaultVisibleFields, pinnedColumns]);

  useEffect(() => {
    apiRef.current.forceUpdate?.();
  }, [pinnedColumns, data]);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel | null>(null);

  const handleVisibilityChange = (newModel: GridColumnVisibilityModel) => {
    setColumnVisibilityModel(newModel);
    onVisibilityModelChange?.(newModel);
  };

  useEffect(() => {
    handleVisibilityChange({
      ...columnVisibilityModel,
      ...columns.reduce((acc, column: any) => {
        acc[column.field] = advancedView ? true : visibleFor(column);
        return acc;
      }, {} as any)
    })
  }, [advancedView])

  // [columns] — (ré)initialise la visibilité. Avec `persistColumnVisibility`, on PRÉSERVE les choix
  // de l'utilisateur (colonnes ajoutées/retirées) et on n'attribue le défaut qu'aux colonnes
  // nouvellement apparues ; sinon comportement historique (reset complet aux défauts à chaque
  // reconstruction de `columns`, qui survient sur refetch data ET sur changement d'evolutionMode).
  useEffect(() => {
    const defaults = columns.reduce((acc, column: any) => {
      acc[column.field] = visibleFor(column);
      return acc;
    }, {} as GridColumnVisibilityModel);
    if (persistColumnVisibility && columnVisibilityModel) {
      const merged = { ...columnVisibilityModel };
      let changed = false;
      Object.keys(defaults).forEach((field) => {
        if (!(field in merged)) {
          merged[field] = defaults[field];
          changed = true;
        }
      });
      if (changed) handleVisibilityChange(merged);
    } else {
      handleVisibilityChange(defaults);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns]);

  // [data] — un refetch (filtre, tri, pagination…) ne doit PAS réinitialiser la visibilité quand
  // la persistance est active. Comportement historique conservé sinon.
  useEffect(() => {
    if (persistColumnVisibility) return;
    handleVisibilityChange({
      ...columnVisibilityModel,
      ...columns.reduce((acc, column: any) => {
        acc[column.field] = advancedView ? true : visibleFor(column);
        return acc;
      }, {} as any)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const Toolbar = (props: any) => {
    return (
      <Stack direction="row" alignItems="center" spacing={0}>
        <GridToolbar {...props} />
        {onExport && (
          <Button startIcon={
            <Iconify icon="lets-icons:export" />
          } sx={{ height: 24 }} color="inherit" onClick={() => onExport(columns)}>
            Export
          </Button>
        )}
      </Stack>
    )
  }

  return (
    <Box
      sx={{
        zIndex: -100,
        width: '100%',
        ...(gridHeight ? { height: gridHeight } : {}),
        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': {
          outline: 'none !important',
        },
        '& .MuiDataGrid-cell--pinnedLeft': {
          backgroundColor: theme.palette.mode === "dark" ? '#212b36 !important' : 'white!important',
        },
        '& .MuiDataGrid-cell': {
          cursor: 'pointer',
        },
      }}
    >
      <DataGridPro
        pinnedColumns={{ left: pinnedColumns }}
        {...(rowHeight ? { rowHeight } : {})}
        {...(columnHeaderHeight ? { columnHeaderHeight } : {})}
        autoHeight={!gridHeight}
        loading={loading}
        disableDensitySelector
        checkboxSelection={false}
        disableRowSelectionOnClick
        columnVisibilityModel={columnVisibilityModel || {}}
        onColumnVisibilityModelChange={handleVisibilityChange}
        rows={data}
        pagination
        paginationMode={paginationMode}
        sortingMode="server"
        disableMultipleColumnsSorting
        rowCount={rowCount}
        filterMode="server"
        pageSizeOptions={[10, 20, 50, 100]}
        showCellVerticalBorder={true}
        sortingOrder={['desc', 'asc', null]}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        filterModel={filterModelProps as GridFilterModel}
        onFilterModelChange={onFilterModelChange}
        columns={columns}
        {...(dimensionFilterField ? {} : { onRowClick: handleRowClick })}
        apiRef={apiRef}
        slots={{
          ...(hideToolbar ? {} : { toolbar: Toolbar }),
          filterPanel: (props) => <FilterPanelCustom {...props} getColumnForNewFilter={(args) => args ? args.columns[0].field : undefined} />,
        }}
        slotProps={{
          toolbar: { printOptions: { disableToolbarButton: true }, csvOptions: { disableToolbarButton: !!onExport } },
          filterPanel: {
            logicOperators: [GridLogicOperator.And, GridLogicOperator.Or],
          },
          // Anchor the columns/filter panel under the call site's custom header button when provided
          // (Explore). Omitted prop → no `panel` key → existing default anchoring is unchanged.
          ...(panelAnchorEl ? { panel: { anchorEl: panelAnchorEl } } : {}),
        }}
      />
    </Box>
  );
}
